/**
 * Socket.io React hook
 * Provides socket connection state and subscription utilities
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import * as socketService from '@/services/socket';
import type { SocketConnectionState } from '@/types/socket';

/**
 * Hook for socket connection state
 */
export function useSocketConnection() {
  const [connectionState, setConnectionState] = useState<SocketConnectionState>(
    socketService.getConnectionState()
  );

  useEffect(() => {
    // Subscribe to connection state changes
    const unsubscribe = socketService.onConnectionStateChange(setConnectionState);

    // Connect on mount
    socketService.connect();

    return () => {
      unsubscribe();
    };
  }, []);

  const connect = useCallback(() => {
    socketService.connect();
  }, []);

  const disconnect = useCallback(() => {
    socketService.disconnect();
  }, []);

  return {
    connectionState,
    isConnected: connectionState === 'connected',
    isConnecting: connectionState === 'connecting',
    isReconnecting: connectionState === 'reconnecting',
    connect,
    disconnect,
  };
}

/**
 * Hook to subscribe to a specific job's updates
 */
export function useJobSubscription(jobId: string | null | undefined) {
  const subscribedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!jobId) return;

    // Subscribe to job updates
    socketService.subscribeToJob(jobId);
    subscribedRef.current = jobId;

    return () => {
      if (subscribedRef.current) {
        socketService.unsubscribeFromJob(subscribedRef.current);
        subscribedRef.current = null;
      }
    };
  }, [jobId]);
}

/**
 * Hook to subscribe to dashboard updates
 */
export function useDashboardSubscription() {
  useEffect(() => {
    socketService.subscribeToDashboard();

    return () => {
      socketService.unsubscribeFromDashboard();
    };
  }, []);
}

/**
 * Hook for job progress events
 */
export function useJobProgressSocket(
  jobId: string | null | undefined,
  onProgress?: (data: Parameters<typeof socketService.onJobProgress>[1] extends (data: infer D) => void ? D : never) => void
) {
  useEffect(() => {
    if (!jobId || !onProgress) return;

    return socketService.onJobProgress(jobId, onProgress);
  }, [jobId, onProgress]);
}

/**
 * Hook for job status events
 */
export function useJobStatusSocket(
  jobId: string | null | undefined,
  onStatus?: (data: Parameters<typeof socketService.onJobStatus>[1] extends (data: infer D) => void ? D : never) => void
) {
  useEffect(() => {
    if (!jobId || !onStatus) return;

    return socketService.onJobStatus(jobId, onStatus);
  }, [jobId, onStatus]);
}

/**
 * Hook for job completion events
 */
export function useJobCompleteSocket(
  jobId: string | null | undefined,
  onComplete?: (data: Parameters<typeof socketService.onJobComplete>[1] extends (data: infer D) => void ? D : never) => void
) {
  useEffect(() => {
    if (!jobId || !onComplete) return;

    return socketService.onJobComplete(jobId, onComplete);
  }, [jobId, onComplete]);
}

/**
 * Hook for job error events
 */
export function useJobErrorSocket(
  jobId: string | null | undefined,
  onError?: (data: Parameters<typeof socketService.onJobError>[1] extends (data: infer D) => void ? D : never) => void
) {
  useEffect(() => {
    if (!jobId || !onError) return;

    return socketService.onJobError(jobId, onError);
  }, [jobId, onError]);
}

/**
 * Hook for cell update events
 */
export function useCellUpdateSocket(
  jobId: string | null | undefined,
  onCellUpdate?: (data: Parameters<typeof socketService.onCellUpdate>[1] extends (data: infer D) => void ? D : never) => void
) {
  useEffect(() => {
    if (!jobId || !onCellUpdate) return;

    return socketService.onCellUpdate(jobId, onCellUpdate);
  }, [jobId, onCellUpdate]);
}

/**
 * Hook for log entry events
 */
export function useLogEntrySocket(
  jobId: string | null | undefined,
  onLogEntry?: (data: Parameters<typeof socketService.onLogEntry>[1] extends (data: infer D) => void ? D : never) => void
) {
  useEffect(() => {
    if (!jobId || !onLogEntry) return;

    return socketService.onLogEntry(jobId, onLogEntry);
  }, [jobId, onLogEntry]);
}

/**
 * Hook for stats update events
 */
export function useStatsUpdateSocket(
  onStatsUpdate?: (data: Parameters<typeof socketService.onStatsUpdate>[0] extends (data: infer D) => void ? D : never) => void
) {
  useEffect(() => {
    if (!onStatsUpdate) return;

    return socketService.onStatsUpdate(onStatsUpdate);
  }, [onStatsUpdate]);
}

/**
 * Combined hook for all job-related socket events
 */
export function useJobSocket(
  jobId: string | null | undefined,
  handlers?: {
    onProgress?: Parameters<typeof socketService.onJobProgress>[1];
    onStatus?: Parameters<typeof socketService.onJobStatus>[1];
    onComplete?: Parameters<typeof socketService.onJobComplete>[1];
    onError?: Parameters<typeof socketService.onJobError>[1];
    onCellUpdate?: Parameters<typeof socketService.onCellUpdate>[1];
    onLogEntry?: Parameters<typeof socketService.onLogEntry>[1];
  }
) {
  // Subscribe to job
  useJobSubscription(jobId);

  // Set up event listeners
  useJobProgressSocket(jobId, handlers?.onProgress);
  useJobStatusSocket(jobId, handlers?.onStatus);
  useJobCompleteSocket(jobId, handlers?.onComplete);
  useJobErrorSocket(jobId, handlers?.onError);
  useCellUpdateSocket(jobId, handlers?.onCellUpdate);
  useLogEntrySocket(jobId, handlers?.onLogEntry);
}

export default useSocketConnection;
